import { useCallback } from 'react';

import type { TagSheetAd, TagSheetTag } from '../../../../types';
import { DEFAULT_NEW_TAG } from './constants';

type UseLineItemDataProps = {
  ads: TagSheetAd[];
  updateAds: (updatedAds: TagSheetAd[]) => void;
};

export type UseLineItemDataReturn = {
  cloneAd: (adIndex: number) => void;
  removeAd: (adIndex: number) => void;
  addTag: (adIndex: number) => void;
  removeTag: (adIndex: number, tagIndex: number) => void;
  editAd: (adIndex: number, updatedAd: TagSheetAd) => void;
  editAdTag: (adIndex: number, tagIndex: number, updatedTag: TagSheetTag) => void;
};

const useLineItemData = ({ ads, updateAds }: UseLineItemDataProps): UseLineItemDataReturn => {
  const cloneAd = useCallback(
    (adIndex: number) => {
      const original = ads[adIndex];

      if (!original) return;

      const cloned: TagSheetAd = {
        ...original,
        id: `${original.id}_${Date.now()}`,
        adName: `${original.adName}_copy`,
        tags: original.tags.map((tag) => ({ ...tag })),
        creative: { ...original.creative },
      };
      const updatedAds = [...ads];

      updatedAds.splice(adIndex + 1, 0, cloned);
      updateAds(updatedAds);
    },
    [ads, updateAds]
  );

  const removeAd = useCallback(
    (adIndex: number) => {
      const updatedAds = ads.filter((_, currentIndex) => currentIndex !== adIndex);

      updateAds(updatedAds);
    },
    [ads, updateAds]
  );

  const addTag = useCallback(
    (adIndex: number) => {
      if (!ads[adIndex]) return;

      const updatedAds = [...ads];

      updatedAds[adIndex] = {
        ...updatedAds[adIndex],
        tags: [...updatedAds[adIndex].tags, { ...DEFAULT_NEW_TAG }],
      };
      updateAds(updatedAds);
    },
    [ads, updateAds]
  );

  const removeTag = useCallback(
    (adIndex: number, tagIndex: number) => {
      if (!ads[adIndex]) return;

      const updatedAds = [...ads];

      updatedAds[adIndex] = {
        ...updatedAds[adIndex],
        tags: updatedAds[adIndex].tags.filter((_, currentTagIndex) => currentTagIndex !== tagIndex),
      };
      updateAds(updatedAds);
    },
    [ads, updateAds]
  );

  const editAd = useCallback(
    (adIndex: number, updatedAd: TagSheetAd) => {
      if (!ads[adIndex]) return;

      const updatedAds = [...ads];

      updatedAds[adIndex] = updatedAd;
      updateAds(updatedAds);
    },
    [ads, updateAds]
  );

  const editAdTag = useCallback(
    (adIndex: number, tagIndex: number, updatedTag: TagSheetTag) => {
      if (!ads[adIndex]) return;
      if (!ads[adIndex].tags[tagIndex]) return;

      const updatedAds = [...ads];
      const updatedTags = [...ads[adIndex].tags];

      updatedTags[tagIndex] = updatedTag;
      updatedAds[adIndex] = { ...ads[adIndex], tags: updatedTags };
      updateAds(updatedAds);
    },
    [ads, updateAds]
  );

  return { cloneAd, removeAd, addTag, removeTag, editAd, editAdTag };
};

export default useLineItemData;
